import {
  type FilterOptions,
  type PaginationMeta,
  type PropertyCard,
  type SearchFacets,
} from '@resolute/schema';
import { keepPreviousData, useQuery } from '@tanstack/react-query';


import { toApiParams, type SearchState } from './search-query';

import { getData, getDataWithMeta } from '@/lib/api-client';


export interface SearchMeta extends PaginationMeta {
  facets: SearchFacets;
}

const searchKeys = {
  all: ['property-search'] as const,
  list: (params: Record<string, string | number>) => [...searchKeys.all, params] as const,
};

/**
 * The choices shown in the search filter panel (cities, property types, BHK
 * configs, statuses, amenities, price bands) — backend-driven via
 * `/filter-options` so they can be relabeled/reordered/hidden through the
 * admin API without a frontend deploy. Long `staleTime` since this changes
 * rarely; a manual `refetch()` (or navigation) picks up admin edits.
 */
export function useFilterOptions() {
  return useQuery({
    queryKey: ['filter-options'] as const,
    queryFn: () => getData<FilterOptions>('/filter-options'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Faceted, paginated property search. `keepPreviousData` keeps the current
 * results visible while the next page/filter loads — no layout flash.
 */
export function usePropertySearch(state: SearchState) {
  const params = toApiParams(state);
  return useQuery({
    queryKey: searchKeys.list(params),
    queryFn: () => getDataWithMeta<PropertyCard[], SearchMeta>('/properties', params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Map view: same filters, but all matching results in one page (up to the API
 * limit) so every marker is plotted. Only fetched when the map is shown.
 */
export function usePropertyMapResults(state: SearchState, enabled: boolean) {
  const params = { ...toApiParams(state), page: 1, limit: 60 };
  return useQuery({
    queryKey: [...searchKeys.list(params), 'map'] as const,
    queryFn: () => getDataWithMeta<PropertyCard[], SearchMeta>('/properties', params),
    placeholderData: keepPreviousData,
    enabled,
  });
}
